import { AfterViewInit, Component, EventEmitter, OnInit, Output } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { OktaAuthStateService } from "@okta/okta-angular";
import { ADPResourceService, CustomFormControl, Resource } from "digital-core";
import { pairwise } from "rxjs";
import { PGHOktaService } from "src/app/core/services/pghokta.service";
import { emailQueryParam } from "src/app/model/constants/query-param.constants";
import { AccountServiceV0 } from "../services/account.service";
import { GoogleAnalyticsService } from "src/app/core/services/google-tag-manager.service";
import { GTMOnboardingEvents, OnboardingSteps } from "src/app/model/google-tag-manager/api-status";

@Component({
  selector: 'app-create-otp-v0',
  templateUrl: './create-otp.component.html',
  styleUrls: ['./create-otp.component.scss']
})
export class CreateOTPComponentV0 implements OnInit {
  @Output() createOTPResponse = new EventEmitter();

  heading: string;
  validationError: string;
  isAuthenticated = false;
  emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  emailControl = new CustomFormControl();
  syntheticToken = '';
  reCaptchaInvisbleKey = '';
  emailResourceData: Resource;

  findContractForm = new UntypedFormGroup({
    emailControl: this.emailControl
  });

  constructor(
    private oktaAuth: OktaAuthStateService,
    private router: Router,
    private route: ActivatedRoute,
    private pghOktaService: PGHOktaService,
    private accountService: AccountServiceV0,
    private googleAnalyticsService: GoogleAnalyticsService,
    public resourceService: ADPResourceService
  ) {
    this.oktaAuth.authState$.subscribe(authState => this.isAuthenticated = authState.isAuthenticated);
    this.route.queryParams.subscribe((params) => {
      if (params['st']) {
        this.syntheticToken = params['st'];
      }
    });
  }

  get isEmailNotValid() : boolean {
    return !this.emailControl.value || !(String(this.emailControl.value).toLowerCase().match(this.emailRegex));
  }

  ngOnInit() {
    this.isAuthenticated = this.pghOktaService.isTokenValid();
    if (this.isAuthenticated) {
      this.router.navigate(['/']);
    }
    else {
      this.route.queryParams.subscribe(params => {
        if (params[emailQueryParam]) {
          this.emailControl.setValue(atob(params[emailQueryParam]));
        }
      });
      this.heading = this.resourceService.getViewLabel('Signup_Create_Account_NewEmail_Heading_Text');
      this.emailResourceData = this.resourceService.getResourceObject('Signup_Create_Account_EmailAddress_Field')
      this.reCaptchaInvisbleKey = this.resourceService.getPortalSetting('Global_ReCaptcha2Invisible').Value;
      this.emailControl.valueChanges.pipe(pairwise()).subscribe(() => this.validateEmailRequired());
    }
  }

  onBlurEmailField() {
    if ((this.emailControl.touched && !this.emailControl.dirty) || !this.emailControl.value) {
      this.validateEmailRequired();
    } else {
      this.validateEmailFormat();
    }
  }

  validateEmailRequired() {
    this.validationError = !this.emailControl.value ? this.resourceService.getViewLabel('Global_Error_Required_Email') : undefined;
  }

  validateEmailFormat() {
    let isValid = !!String(this.emailControl.value).toLowerCase().match(this.emailRegex);
    this.validationError = !isValid ? this.resourceService.getViewLabel('Global_Error_Valid_Email') : undefined;
  }

  recaptchaExecute(recaptcha) {
    // NOTE: This is to verify error as the button is always enabled
    this.validationError || this.validateEmailRequired();
    this.validationError || this.validateEmailFormat();
    if (this.validationError) {
      return;
    }
    // NOTE: Disable recaptcha to enable testing
    let isRecpatchEnabled = this.resourceService.isPortalSettingTrue('Global_Recaptcha_Feature');
    if (!isRecpatchEnabled || this.syntheticToken !== '') {
      this.createAccount(this.syntheticToken, recaptcha);
    } else {
      recaptcha.execute();
    }
  }

  createAccount(token, reCaptcha) {
    this.accountService.accountLookup(this.emailControl.value, token).subscribe({
      next: (response: any) => {
        if (response?.encryptedOTP) {
          response['reqEmail'] = this.emailControl.value;
          this.createOTPResponse.emit(response);
          this.validationError = undefined;
          this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Start, OnboardingSteps.Start);
        } else {
          this.validationError = this.resourceService.getViewLabel('Global_Modal_Error_Title');
        }
      },
      error: (err) => {
        reCaptcha.reset();
        if (err.error && (err.error.ErrorCode == 'NoContractForEmail' || err.error.Message == 'Search error')) {
          this.validationError = this.resourceService.getViewLabel('Signup_Create_Account_NotFound_Error');
        } else if (err.error && err.error.ErrorCode == 'OktaUserAlreadyExist') {
          this.validationError = this.resourceService.getViewLabel('Signup_Create_Account_EmailExist_Error');
        } else {
          throw err;
        }
      }
    });
  }

  goToLogin() {
    // TODO: investigate why the login form doesn't load when the user navigates back to the login screen
    const loginRoute = this.resourceService.getPortalSetting('URL_Login').Value;
    window.location.replace(loginRoute);
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
